import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ExternalLinkIcon, NewspaperIcon, Share2Icon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BLOG_POSTS, BlogPost } from '../data/blog-posts';
import { Navbar } from '../components/Navbar';
import { StoryBody } from '../components/shared/StoryBody';
import { getCategoryColor, getPostNum, resolveTranslatedField, translateCategory } from '../utils/blog-helpers';
export function NewsStoryPage({
  postId
}: {
  postId: string;
}) {
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [postId]);
  // Keep URL hash in sync with current language so shared links preserve it
  useEffect(() => {
    const newHash = `#/news/${postId}?lang=${language}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [language, postId]);
  const post = BLOG_POSTS.find(p => p.id === postId);
  if (!post) {
    return <div className="font-nunito w-full min-h-screen overflow-x-hidden bg-gray-50" data-id="element-4012" data-mp="2jwc2ci20p2mc">
        <Navbar data-id="element-4013" data-mp="2f1xzxvoxtv86" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-id="element-4014" data-mp="2f1g1csesz4x3">
          <h1 className="font-bebas text-4xl text-haa-navy tracking-wider mb-4" data-id="element-4015" data-mp="2a461sjxp9cvj">
            {isEs ? 'Historia no encontrada' : 'Story Not Found'}
          </h1>
          <p className="font-nunito text-gray-500 mb-8" data-id="element-4016" data-mp="213hv6aamyeso">
            {isEs ? 'La historia que buscas no existe o ha sido movida.' : "The story you're looking for doesn't exist or has been moved."}
          </p>
          <a href="#" onClick={e => {
          e.preventDefault();
          window.location.hash = '';
        }} className="inline-flex items-center gap-2 bg-haa-blue text-white font-nunito font-bold text-sm px-6 py-3 rounded-full hover:bg-haa-midblue transition-colors" data-id="element-4017" data-mp="1r7ejodtw93t2">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-4018" data-mp="1okvr5a50o4qh" />
            {isEs ? 'Volver al Inicio' : 'Back to Home'}
          </a>
        </main>
      </div>;
  }
  const postNum = getPostNum(post.id);
  const translatedTitle = resolveTranslatedField(t, postNum, 'title', post.title);
  const translatedDate = resolveTranslatedField(t, postNum, 'date', post.date);
  const translatedFullStory = resolveTranslatedField(t, postNum, 'fullStory', post.fullStory);
  const translatedCategory = translateCategory(t, post.category);
  const handleCopyLink = () => {
    const base = window.location.origin + window.location.pathname;
    const url = `${base}#/news/${postId}?lang=${language}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden bg-gray-50" data-id="element-4019" data-mp="m7q16m8vao40">
      <Navbar data-id="element-4020" data-mp="30dmz50equ98a" />
      <main className="pt-24 pb-16" data-id="element-4021" data-mp="30dd3aw70p7uo">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4022" data-mp="30gitkerdufrz">
          {/* Back link */}
          <motion.div initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-4023" data-mp="2vhfr922sennp">
            <a href="#" onClick={e => {
            e.preventDefault();
            window.location.hash = '';
            setTimeout(() => {
              const el = document.getElementById('news');
              if (el) {
                el.scrollIntoView({
                  behavior: 'smooth'
                });
              }
            }, 200);
          }} className="inline-flex items-center gap-2 font-nunito text-haa-blue hover:text-haa-midblue text-sm font-semibold transition-colors mb-8" data-id="element-4024" data-mp="2hoq0kkcbcuk9">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-4025" data-mp="26p652mitswkb" />
              {isEs ? '← Volver a Noticias' : '← Back to News'}
            </a>
          </motion.div>

          {/* Image */}
          {post.image && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-8" data-id="element-4026" data-mp="2gkg7vch1t7z5">
              <img src={post.image} alt={translatedTitle} className="w-full max-h-[28rem] object-cover rounded-2xl shadow-lg" style={{
            objectPosition: post.imagePosition || 'center center'
          }} data-id="element-4027" data-mp="2zc90h5s7gr6c" />
              {post.imageCaption && <p className="mt-3 font-nunito text-sm italic text-gray-600 leading-relaxed" data-id="element-4028" data-mp="19y3vvsfjwh8m">
                  {isEs && post.imageCaptionEs ? post.imageCaptionEs : post.imageCaption}
                </p>}
            </motion.div>}

          {/* Video Embeds — shown at top when no image */}
          {!post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-8" data-id="element-4029" data-mp="nmstmz3e6pt1">
              {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-2xl overflow-hidden shadow-lg bg-black" data-id="element-4030" data-mp="16f54ybc0x1fb" data-mpi={vi}>
                  {video.youtubeId ? <div className="relative w-full" style={{
              paddingBottom: '56.25%'
            }} data-id="element-4031" data-mp="3r42kb2g0e5cg">
                      <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                border: 0
              }} data-id="element-4032" data-mp="2n78mjknm2ghr" />
                    </div> : null}
                </div>)}
            </motion.div>}

          {/* Category + Date */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="flex flex-wrap items-center gap-3 mb-5" data-id="element-4033" data-mp="1gito1qk66er9">
            <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-4034" data-mp="xxdzm4vlvq8u">
              {translatedCategory}
            </span>
            <span className="flex items-center gap-1.5 text-gray-400 text-sm" data-id="element-4035" data-mp="1kuz3axpdt211">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-4036" data-mp="1gtvo8df5ra0v" />
              <span className="font-nunito" data-id="element-4037" data-mp="1gtdsrj0bkzu0">{translatedDate}</span>
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="font-bebas text-3xl sm:text-4xl lg:text-5xl text-haa-navy tracking-wider leading-tight mb-8" data-id="element-4038" data-mp="1un4x8cz71tiv">
            {translatedTitle}
          </motion.h1>

          {/* Full Story */}
          {translatedFullStory && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mb-10" data-id="element-4039" data-mp="u5yy1h2i3blm">
              <StoryBody text={translatedFullStory} className="prose prose-lg max-w-none font-nunito text-gray-600 leading-relaxed whitespace-pre-line" data-id="element-4040" data-mp="6eba980jcgnk" />
            </motion.div>}

          {/* Story Images Gallery */}
          {post.storyImages && post.storyImages.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="mb-10 space-y-4" data-id="element-4041" data-mp="3jur7slpgzn8k">
              {post.storyImages.map((img, idx) => <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-id="element-4042" data-mp="1n0538i0z4m9y" data-mpi={idx}>
                  <img src={img} alt={`${translatedTitle} — ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-4043" data-mp="1i2lozi4a09i0" />
                </div>)}
            </motion.div>}

          {/* Video Embeds — shown after story when post has an image */}
          {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.25
        }} className="mb-10" data-id="element-4044" data-mp="zakmm5sam13u">
              <h2 className="font-bebas text-2xl text-haa-navy tracking-wider mb-4" data-id="element-4045" data-mp="35vpzuoei3a6s">
                {isEs ? 'Videos' : 'Videos'}
              </h2>
              <div className="grid grid-cols-1 gap-4" data-id="element-4046" data-mp="31uecyqn0h32a">
                {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-2xl overflow-hidden shadow-lg bg-black" data-id="element-4047" data-mp="2e5v7sjz9d6ef" data-mpi={vi}>
                    {video.youtubeId ? <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }} data-id="element-4048" data-mp="2n2m32u5zkh7y">
                        <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                  border: 0
                }} data-id="element-4049" data-mp="3tksnccvtfx97" />
                      </div> : null}
                    <div className="px-4 py-3 bg-gray-900" data-id="element-4050" data-mp="oozn8omryqmp">
                      <p className="font-nunito text-white/80 text-xs font-semibold truncate" data-id="element-4051" data-mp="tky6xyx5rkgs">
                        {video.label}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>}

          {/* Coverage Links */}
          {post.coverageLinks && post.coverageLinks.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.25
        }} className="pt-6 border-t border-gray-200 mb-8" data-id="element-4052" data-mp="1ccr0bug537p1">
              <div className="flex items-center gap-2 mb-3" data-id="element-4053" data-mp="1897y9imcb2hc">
                <NewspaperIcon className="w-4 h-4 text-gray-400" data-id="element-4054" data-mp="18arexabwrfjn" />
                <span className="font-nunito text-gray-500 text-sm uppercase tracking-wider font-bold" data-id="element-4055" data-mp="13adjqsveo031">
                  {t('news.alsoCoveredBy')}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3" data-id="element-4056" data-mp="tktxdd8hho3f">
                {post.coverageLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white hover:bg-gray-100 font-nunito text-sm font-bold text-haa-midblue hover:text-haa-blue transition-colors border border-gray-200 shadow-sm" data-id="element-4057" data-mp="oma8p52i9y7d" data-mpi={i}>
                    {link.label}
                    <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-4058" data-mp="307j8d839baxq" />
                  </a>)}
              </div>
            </motion.div>}

          {/* Share Button */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.3
        }} className="pt-6 border-t border-gray-200 flex items-center gap-4" data-id="element-4059" data-mp="28h4yro2a0krv">
            <button onClick={handleCopyLink} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito font-bold text-sm transition-all duration-200 shadow-sm ${copied ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200 text-haa-blue hover:bg-haa-blue hover:text-white hover:border-haa-blue'}`} data-id="element-4060" data-mp="2ma4e9q226ezf">
              {copied ? <>
                  <CheckIcon className="w-4 h-4" data-id="element-4061" data-mp="91bju95orh98" />
                  {isEs ? '¡Enlace copiado!' : 'Link copied!'}
                </> : <>
                  <Share2Icon className="w-4 h-4" data-id="element-4062" data-mp="48qubjttbqbi" />
                  {isEs ? 'Copiar enlace' : 'Copy link'}
                </>}
            </button>
          </motion.div>
        </div>
      </main>
    </div>;
}